// Temp disable user flow per Product request, remove lint rule override when re-enabled
/* eslint-disable no-unused-vars */
import React from "react";
import PropTypes from "prop-types";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { isEqual, find, pick, map } from "@utils/lodash";
import { ToastContainer } from "react-toastr";
import PubNub from "pubnub";
import { PubNubProvider } from "pubnub-react";
import GlobalsActions from "@redux/globals";
import AppActions, { thunks } from "@redux/app";
import AccountActions from "@redux/account";
import { MAIN_MENU, ENV_OPTIONS, USER_TYPE, ROUTES, URLS } from "@constants";
import Utils from "@utils";
import { AccountService, UserFlowService, SalesforceService } from "@services";
import { withMsal } from "@azure/msal-react";
import AnalyticsService from "@services/AnalyticsService";
import { ANALYTICS_EVENTS_ACTIONS, ANALYTICS_EVENTS_CATEGORIES } from "@constants/analyticsEvents";
import { Navbar, JobChat, SplashScreen, ErrorBoundary } from "../../../components";
import Announcement from "./Announcement";

const SESSION_CHECKER_TIMER = 30000;

class AuthWrapper extends React.PureComponent {
	constructor(props) {
		super(props);

		AnalyticsService.initialize();

		/**
		 * Define publicUrls in the app
		 */
		this.publicUrls = {
			login: "/login",
			logout: "/logout",
			signup: "/signup",
			signuplea: "/signup-lea",
			signupcoordinator: "/signup-coordinator",
			confirmation: "/confirmation",
			activation: "/activation",
		};

		/**
		 * Define what is the root/home/index path
		 * of a user based on its role.
		 *
		 * IMPORTANT:
		 * Make sure you have the role in allowedRoles
		 * of the corresponding route definition, otherwise
		 * it'll be an infinite redirection loop, because the
		 * app will try to redirect to rootPath of the role
		 * while checking if it's allowed as well to that path.
		 */
		this.rootPathByRole = {
			[USER_TYPE.SUPER_ADMIN]: "/jobs",
			[USER_TYPE.AGENCY_ADMIN]: "/jobs",
			[USER_TYPE.LAW_ENFORCEMENT]: "/dashboard",
			[USER_TYPE.COORDINATOR]: "/dashboard",
		};

		const initialState = {
			requestedPath: "",
			requestedRoute: undefined,
			authenticated: false,
			isSessionChecked: false,
			pubnub: undefined,
			announcements: [],
			previewingAnnouncement: false,
		};

		const state = { ...initialState };
		const route = props.routes[1];
		const isPublicPage = this.isPublicPage();

		state.requestedRoute = !isPublicPage ? route : undefined;
		state.requestedPath = !isPublicPage ? window.location.pathname : "/";

		this.state = state;
		this.initialState = initialState;
	}

	componentDidMount() {
		const { routes, params, location, setRouteDetails } = this.props;
		const route = routes[1];

		// Update setRouteDetails
		setRouteDetails(params, map(routes, "path"));

		// Register unauthorized event
		Utils.registerEventListener("unauthorized", this.unauthorized);

		// Set active link
		this.setActiveLink(`/${route.path || ""}`);

		// Track initial page
		this.trackPage(location.pathname);

		/**
		 * Check session when the
		 * component did mount
		 */
		this.checkSession();
		this.initializeListeners();
	}

	// When attempting to move all functionality from this method to `componentDidUpdate`
	// The app experience navigation issues related to jobs and payment list, since those pages
	// still use a heavy amount of redux. Refactoring those pages as well as the app routing strategy should
	// eliminate the need for this soon.
	UNSAFE_componentWillReceiveProps(nextProps) {
		const { routes, params, setRouteDetails } = nextProps;
		const nextRoute = routes[1];
		const lastProps = this.props;
		const prevRoute = lastProps.routes[1];
		const nextLocation = nextProps.location;
		const prevLocation = lastProps.location;

		// Update route details state
		if (!isEqual(params, lastProps.params) || !isEqual(routes, lastProps.routes)) {
			setRouteDetails(params, map(routes, "path"));
		}

		// Set active link
		if (nextRoute && prevRoute && nextRoute.path !== prevRoute.path) {
			this.setActiveLink(`/${nextRoute.path || ""}`, nextProps);
		}

		if (prevLocation.pathname === this.publicUrls.login && nextLocation.action === "POP") {
			nextProps.router.push(this.publicUrls.login);
		}

		// Track page on change
		if (nextLocation.pathname !== prevLocation.pathname) {
			this.trackPage(nextLocation.pathname);
		}
	}

	// It looks as through this method is actually meant to execute on mount as well, but this lifecycle method does not begin
	// executing until the component has been mounted for the first time. Moving this logic to componentDidMount could have unintented
	// side effects, so this will have to be reviewed and discussed further before any changes are made.
	UNSAFE_componentWillUpdate(nextProps) {
		const { lang } = global;
		const { authenticated } = this.state;
		const { routes } = nextProps;
		const requestedRoute = routes[1];

		/**
		 * Prevent open the login if is logged in
		 * or an unauthorized page
		 */
		if (authenticated && (this.isPublicPage() || !this.isAllowed(requestedRoute))) {
			if (!this.isAllowed(requestedRoute)) {
				Utils.notify({
					type: "error",
					title: lang.t("Access Denied"),
					message: `${lang.t("You are not allowed on")} /${requestedRoute.path}`,
				});
			}

			const { router } = this.props;
			router.push(this.getRootPath());
		}
	}

	componentDidUpdate({ user: prevUser }) {
		const { user } = this.props;
		// The current user has either logged out or been logged out for one reason or another
		if (prevUser !== user && user === null) {
			this.closePubnubInstance();
		}
	}

	initializeListeners = () => {
		document.addEventListener("visibilitychange", this.handleSession);
		document.addEventListener("notificationReceived", event => {
			const {
				detail: {
					notification,
					fcmOptions: { link },
				},
			} = event;
			const { user, router } = this.props;
			const isChatMention = link && link.includes("#openChat");
			if (isChatMention) {
				this.initializeChat(user, pubnub => {
					const jobId = link.split("/")[2].replace("#openChat", "");
					AnalyticsService.trackEvent({
						category: ANALYTICS_EVENTS_CATEGORIES.JOB_CHAT,
						action: ANALYTICS_EVENTS_ACTIONS.JOB_CHAT_OPENED,
						customParameters: {
							jobEventId: jobId,
						},
					});
					// Trigger a subscription action to either become a member of this chat or open the chat window to this job automatically
					pubnub.subscribe({ channels: [`job-${jobId}`] });
				});
			}

			this._notificationPush.info(
				<button
					type="button"
					className="message-wrapper"
					onClick={() => router.push(link)}
				>
					<If condition={notification.icon}>
						<img
							alt="Notification icon"
							src={notification.icon}
							className={isChatMention ? "img-circle" : undefined}
						/>
					</If>
					<div className="message-body">{notification.body}</div>
				</button>,
				<div className="title-wrapper">
					<div className="rkf rkf-rollkall-shield logo" />
					<div>{notification.title}</div>
				</div>,
				{
					timeOut: 15000,
					extendedTimeOut: 3000,
					showAnimation: "animated slideInRight",
					hideAnimation: "animated fadeOut",
					closeButton: true,
				},
			);
		});
	};

	/** Initializes a new PubNub chat instance, assuming one did not already exist before.
	 * If one is already active, any callback logic is executed using the existing instance.
	 * @param {Object} user The user object with which to instantiate PubNub
	 * @param {Function} callback A callback function to handle any logic necessary once a PubNub instance exists
	 */
	initializeChat = (user, callback) => {
		const { pubnub: oldPubnub } = this.state;
		if (oldPubnub === undefined) {
			const pubnub = new PubNub({
				publishKey: ENV_OPTIONS.pubnubPublishKey,
				subscribeKey: ENV_OPTIONS.pubnubSubscribeKey,
				userId: String(user.Id),
			});
			this.setState({ pubnub }, () => {
				if (callback) callback(pubnub);
			});
		} else if (callback) callback(oldPubnub);
	};

	/** Closes the open PubNub instance if there is one. Should only be called on logout (`user === null`) */
	closePubnubInstance = () => {
		const { pubnub } = this.state;
		if (pubnub) {
			pubnub.stop();
			this.setState({ pubnub: undefined });
		}
	};

	checkTimeZone = user => {
		const { lang } = global;
		const userDate = Utils.formatDate(new Date(), undefined, user?.TimeZoneId, "dt-num");
		const systemDate = Utils.formatDate(new Date(), undefined, undefined, "dt-num");

		/**
		 * If the localTimeZone is the same
		 * than the user's timeZone, we don't
		 * need to do anything.
		 *
		 * But if it's different, then we show
		 * a notification about it
		 */
		if (userDate === systemDate) return;

		Utils.notify({
			type: "info",
			title: `${lang.t("Your local timezone is different than your account timezone")}
			${lang.t("Go to account settings if you need to update your default timezone")}`,
		});
	};

	handleSession = () => {
		const { isSessionChecked } = this.state;

		if (!document.hidden && !isSessionChecked) {
			this.checkSession(true);

			this.setState({ isSessionChecked: true }, () => {
				setTimeout(() => {
					this.setState({ isSessionChecked: false });
				}, SESSION_CHECKER_TIMER);
			});
		}
	};

	setActiveLink = (pathname, props = this.props) => {
		const { updateActiveLink } = props;

		updateActiveLink("subMenu", 0);
		updateActiveLink(
			"mainMenu",
			MAIN_MENU.findIndex(link => link.url === pathname),
		);
	};

	trackPage = page => {
		const { user } = this.props;

		AnalyticsService.trackPage(page, user);
	};

	handleUnauthorized = inBackground => {
		const {
			router,
			location: { pathname },
		} = this.props;

		let targetPage = this.getPublicPathName() || this.publicUrls.login;
		const userId = localStorage.getItem("userId");

		if (inBackground) {
			this.showSessionExpiredError();
		}

		this.setState({ authenticated: false });

		if (pathname === this.publicUrls.confirmation && !userId) {
			targetPage = this.publicUrls.login;
		}

		router.push(targetPage);
	};

	checkSession = inBackground => {
		const { authenticated } = this.state;

		if (!authenticated && inBackground) {
			return;
		}

		AccountService.me()
			.then(({ data }) => {
				this.setState({ announcements: data.Announcements });
				this.allowAccess(data, inBackground);
			})
			.catch(() => {
				this.handleUnauthorized(inBackground);
			});
	};

	getPublicPathName = () => {
		const { location } = this.props;
		const pathName = find(this.publicUrls, page => page === location.pathname);

		return pathName !== this.publicUrls.logout ? pathName : undefined;
	};

	isPublicPage = () => this.getPublicPathName() !== undefined;

	isInLogout = () => {
		const { location } = this.props;
		return location.pathname === this.publicUrls.logout;
	};

	isInLogin = () => {
		const { location } = this.props;
		return location.pathname === this.publicUrls.login;
	};

	allowAccess = (user, inBackground) => {
		const {
			dispatch,
			router,
			receiveUser,
			receiveAgency,
			setProfilePic,
			location: { query },
			ldClient,
		} = this.props;
		const { UserTypeId, Email } = user;

		window.userId = user.Id;
		receiveUser(user);

		/**
		 * If the user is an officer or client, we redirect
		 * to the rollkall portal.
		 */
		if ([USER_TYPE.OFFICER, USER_TYPE.CLIENT].includes(UserTypeId)) {
			this.unauthorized();

			window.location = ROUTES.rollkallPortalSilentLogin;
			return;
		}

		/*
		 * The getPaymentMethods() request
		 * is not allowed to admins, because
		 * they don't have any payment options.
		 */
		if (
			user.UserTypeId !== USER_TYPE.SUPER_ADMIN &&
			user.UserTypeId !== USER_TYPE.LAW_ENFORCEMENT
		) {
			dispatch(thunks.getPaymentMethods());
		}

		receiveAgency(user.Organization);
		setProfilePic(user.Organization.OrganizationLogo);

		const { requestedRoute, requestedPath } = this.state;
		const { logout } = this.publicUrls;

		if (inBackground) {
			/**
			 * If the current userTypeId is not
			 * allowed to see this portal, we show
			 * the session expired error, and logout
			 * the user
			 */
			if (!this.rootPathByRole[UserTypeId]) {
				this.showSessionExpiredError();
				window.userId = undefined;
				router.push(logout);
			}
			return;
		}

		// Check user's timeZone
		this.checkTimeZone(user);

		let pathname = requestedPath;
		if (this.isPublicPage() || !this.isAllowed(requestedRoute)) {
			pathname = this.getRootPath();
		}

		if (pathname === logout && !requestedRoute) {
			this.showUserNotAllowedError();
		}

		// Init Userflow service
		UserFlowService.initUserFlow(user);

		// Init Salesforce service
		SalesforceService.initSalesforce(user.UserTypeId);

		const ddUser = {
			id: user.Id,
			name: `${user.FirstName} ${user.LastName}`,
			userTypeId: user.UserTypeId,
		};

		// Link user with datadog
		datadogRum.setUser(ddUser);
		datadogLogs.setUser(ddUser);

		// Set LaunchDarkly context
		ldClient.identify({
			key: user.Id,
			firstName: user.FirstName,
			lastName: user.LastName,
			email: user.Email,
			custom: {
				organizationId: user.Organization?.Id,
				userTypeId: user.UserTypeId,
			}
		}).finally(() => {
			this.setState({ authenticated: true }, () => {
				router.push({ pathname, query });
			});
		});
	};

	showSessionExpiredError = () => {
		const { lang } = global;
		Utils.notify({
			type: "error",
			title: lang.t("Session Expired"),
			message: lang.t("Session Expired"),
		});
	};

	showUserNotAllowedError = () => {
		const { lang } = global;
		Utils.notify({
			type: "error",
			title: lang.t("Access Denied"),
			message: lang.t("This user is not allowed to use this portal"),
		});
	};

	isAllowed = requestedRoute => {
		const { user } = this.props;
		// Allow other paths such as logout when no user exists
		return requestedRoute && user ? user.canNavigate(requestedRoute.path) : true;
	};

	getRootPath = () => {
		const { user } = this.props;
		const { logout } = this.publicUrls;

		/**
		 * If it's not defined in rootPathByRole
		 * means that is not allowed, so the root
		 * path is logout by default
		 */

		if (!this.rootPathByRole[user.UserTypeId]) {
			return logout;
		}

		if (user.IsViewOnly) {
			return "/jobs";
		}

		return this.rootPathByRole[user.UserTypeId];
	};

	/**
	 * Handle unauthorized events.
	 *
	 * Note: This can only happen if we are running with adb2c
	 */
	unauthorized = () => {
		const {
			router,
			resetUser,
			msalContext: { instance: msalInstance },
		} = this.props;
		const { login } = this.publicUrls;

		msalInstance.browserStorage.clear().finally(() => {
			localStorage.removeItem("homeAccountId");

			this.setState(this.initialState, () => {
				router.push(login);
			});

			resetUser();
		});
	};

	notify = ({
		type = "info",
		title = "",
		message = "",
		closeButton = false,
		timeOut = 6000,
		extendedTimeOut = null,
		showAnimation = "animated flipInX",
		hideAnimation = "animated flipOutX",
	}) => {
		const optionsOverride = {
			closeButton,
			timeOut,
			extendedTimeOut,
			showAnimation,
			hideAnimation,
		};

		this._notificationSystem[type](message, title, optionsOverride);
	};

	onLogout = () => {
		const {
			resetUser,
			msalContext: { instance: msalInstance },
		} = this.props;
		const { login } = this.publicUrls;

		localStorage.removeItem("homeAccountId");
		msalInstance.logoutRedirect({ postLogoutRedirectUri: ROUTES.login });

		resetUser();
	};

	previewAnnouncement = (messages, callback) => {
		this.setState(
			{ announcements: messages, previewingAnnouncement: messages.length > 0 },
			() => {
				if (callback) callback();
			},
		);
	};

	render() {
		const { authenticated, announcements, pubnub, previewingAnnouncement } = this.state;
		const { routes, children, user } = this.props;

		return (
			<div id="auth-wrapper-content">
				<Announcement
					data={announcements}
					previewMode={previewingAnnouncement}
				/>
				<Choose>
					<When
						condition={
							(!authenticated && !this.isPublicPage()) || !this.isAllowed(routes[1])
						}
					>
						<SplashScreen />
					</When>
					<Otherwise>
						<If condition={authenticated && !this.isInLogout()}>
							<Navbar user={user} />
						</If>
						<div id="app-content">
							{React.Children.map(children, child =>
								React.cloneElement(child, {
									onLogin: !authenticated ? this.allowAccess : undefined,
									onLogout: authenticated ? this.onLogout : undefined,
									initializeChat: this.initializeChat,
									previewAnnouncement:
										authenticated && user?.isSuperAdmin()
											? this.previewAnnouncement
											: undefined,
									pubnub,
									user,
								}),
							)}
							<If condition={user && pubnub}>
								<PubNubProvider client={pubnub}>
									<JobChat userId={user.Id} />
								</PubNubProvider>
							</If>
						</div>
					</Otherwise>
				</Choose>
				<ToastContainer
					ref={ref => {
						this._notificationPush = ref;
					}}
					preventDuplicates={false}
					newestOnTop={false}
					className="push-notification toast-bottom-right"
				/>
			</div>
		);
	}
}

const mapStateToProps = ({ app: { user } }) => ({
	user,
});

const mapDispatchToProps = dispatch => ({
	dispatch,
	...pick(bindActionCreators(AppActions, dispatch), [
		"resetUser",
		"receiveUser",
		"updateActiveLink",
	]),
	...pick(bindActionCreators(AccountActions, dispatch), ["receiveAgency", "setProfilePic"]),
	setRouteDetails: bindActionCreators(GlobalsActions, dispatch).setRouteDetails,
});

AuthWrapper.propTypes = {
	routes: PropTypes.array.isRequired,
	params: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
	setRouteDetails: PropTypes.func.isRequired,
	router: PropTypes.object.isRequired,
	user: PropTypes.object,
	dispatch: PropTypes.func.isRequired,
	receiveUser: PropTypes.func.isRequired,
	receiveAgency: PropTypes.func.isRequired,
	setProfilePic: PropTypes.func.isRequired,
	resetUser: PropTypes.func.isRequired,
	children: PropTypes.any.isRequired,
	msalContext: PropTypes.object.isRequired,
	ldClient: PropTypes.object.isRequired,
};

AuthWrapper.defaultProps = {
	user: {},
};

export default withLDConsumer()(
	withMsal(connect(mapStateToProps, mapDispatchToProps)(AuthWrapper)),
);
